// ============================================================================
// Hire delivery windows — mirror of Brand-Dashboard/worker/src/deadlines.ts
// ============================================================================
// The window a creator gets to deliver scales with package size:
//
//   1 video → 10 days · 3 videos → 14 days
//   5 videos → 14 days · 10 videos → 21 days
//   20 videos → 30 days · 30 videos → 35 days
//
// The worker is the source of truth (it stamps cr_hires.deadline_at on
// confirm). Use this only to *project* a window before a hire exists — offer
// previews, package pickers, apply pages. Once a hire is confirmed, always
// render the stored deadline_at instead of recomputing it, since a granted
// extension moves the real date and this function knows nothing about that.
// Keep the tiers in sync with the worker if they change.
// ============================================================================

export const HIRE_DEADLINE_TIERS: ReadonlyArray<{ upToVideos: number; days: number }> = [
  { upToVideos: 1, days: 10 },
  { upToVideos: 3, days: 14 },
  { upToVideos: 5, days: 14 },
  { upToVideos: 10, days: 21 },
  { upToVideos: 20, days: 30 },
  { upToVideos: 30, days: 35 },
]

/** Window for hires whose package size we can't resolve (pre-tier rows). */
export const FALLBACK_HIRE_DEADLINE_DAYS = 30

/** Days a creator gets to deliver a package of `packageSize` videos. */
export function deadlineDaysForPackage(packageSize: number | null | undefined): number {
  const size = Number(packageSize)
  if (!Number.isFinite(size) || size <= 0) return FALLBACK_HIRE_DEADLINE_DAYS
  for (const tier of HIRE_DEADLINE_TIERS) {
    if (size <= tier.upToVideos) return tier.days
  }
  return HIRE_DEADLINE_TIERS[HIRE_DEADLINE_TIERS.length - 1].days
}

/** Projected deadline for a package if it were confirmed right now. */
export function projectedDeadlineFor(
  packageSize: number | null | undefined,
  fromMs: number = Date.now()
): Date {
  return new Date(fromMs + deadlineDaysForPackage(packageSize) * 86400000)
}

// ============================================================================
// Post-deadline submission grace period
// ============================================================================
//
// A creator isn't locked out the instant deadline_at passes — they keep a 48h
// window to still submit videos or request an extension. That makes it a window
// in which the brand's money is NOT refundable: refunding there could leave the
// brand holding both the videos and the credit, or leave an extended hire with
// nothing collected against it.
//
// Enforced in wallet_refund_hire_to_credit (see
// creator-app/supabase/migrations/20260806000000_wallet_refund_respects_grace_period.sql)
// — this mirror only exists so the admin UI can show the countdown and explain
// the wait without a round-trip. Mirror the constant in
// Brand-Dashboard/worker/src/deadlines.ts, Brand-Dashboard/src/lib/deadlines.ts
// and creator-app/utils/deadlines.ts if it ever changes; all four must move
// together.
// ============================================================================

export const SUBMISSION_GRACE_PERIOD_HOURS = 48
const SUBMISSION_GRACE_PERIOD_MS = SUBMISSION_GRACE_PERIOD_HOURS * 60 * 60 * 1000

/** Parsed deadline in ms, or null when absent/unparseable. Every predicate below
 *  goes through this so they can't disagree about what a bad date means. */
function deadlineMs(deadlineAt: string | null | undefined): number | null {
  if (!deadlineAt) return null
  const t = new Date(deadlineAt).getTime()
  return isNaN(t) ? null : t
}

/** When the grace window closes, or null if there's no deadline to count from. */
export function gracePeriodEndsAt(deadlineAt: string | null | undefined): Date | null {
  const t = deadlineMs(deadlineAt)
  return t == null ? null : new Date(t + SUBMISSION_GRACE_PERIOD_MS)
}

/** True once `deadlineAt` has passed but the 48h grace window is still open. */
export function isWithinGracePeriod(deadlineAt: string | null | undefined, nowMs: number = Date.now()): boolean {
  const t = deadlineMs(deadlineAt)
  if (t == null) return false
  return nowMs >= t && nowMs <= t + SUBMISSION_GRACE_PERIOD_MS
}

/** True once the 48h post-deadline grace window has fully elapsed. */
export function isPastGracePeriod(deadlineAt: string | null | undefined, nowMs: number = Date.now()): boolean {
  const t = deadlineMs(deadlineAt)
  if (t == null) return false
  return nowMs > t + SUBMISSION_GRACE_PERIOD_MS
}

/**
 * Ms remaining in the grace window — negative once it has elapsed, null with no
 * deadline. Signed and unrounded on purpose: the live countdown needs to render
 * hours AND minutes, and needs to see the value cross zero.
 */
export function graceMsRemaining(deadlineAt: string | null | undefined, nowMs: number = Date.now()): number | null {
  const t = deadlineMs(deadlineAt)
  return t == null ? null : t + SUBMISSION_GRACE_PERIOD_MS - nowMs
}

/**
 * Whole hours left, rounded up, floored at 0. The coarse form used in prose
 * ("Refund opens in 31h") where a ticking mm:ss would be noise. Distinct from
 * graceMsRemaining, which the countdown component needs.
 */
export function graceHoursRemaining(deadlineAt: string | null | undefined, nowMs: number = Date.now()): number {
  const ms = graceMsRemaining(deadlineAt, nowMs)
  if (ms == null) return 0
  return Math.max(0, Math.ceil(ms / 3600000))
}
